import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#111216]">
      <SEO 
        title="Privacy Policy | Detailing Disciples"
        description="Detailing Disciples privacy policy and data practices."
        canonical="https://thedetailingdisciples.com/privacy"
      />

      <section className="pt-32 pb-16 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <Shield className="w-12 h-12 text-[#B8860B]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white text-center mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-400 text-center mb-8">
              Last Updated: December 2025
            </p>
            <div className="bg-yellow-900/20 border border-yellow-900/40 rounded-xl p-6 mb-8">
              <p className="text-yellow-200 text-sm">
                ⚠️ <strong>OWNER ACTION REQUIRED:</strong> This privacy policy template must be reviewed by the business owner and/or legal counsel before publication. Customize all sections to reflect actual business practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 bg-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 space-y-8 text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
              <p className="mb-4">
                When you request a quote or book a service through Detailing Disciples, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Name, phone number, and email address</li>
                <li>Vehicle year, make, model, and color</li>
                <li>Service location (ZIP code or city)</li>
                <li>Service preferences and vehicle condition details</li>
                <li>Photos you voluntarily upload for quote assessment</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Your Information</h2>
              <p className="mb-4">We use the information collected to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide personalized service quotes and recommendations</li>
                <li>Schedule and confirm appointments</li>
                <li>Communicate about your vehicle and service updates</li>
                <li>Process payments (if applicable)</li>
                <li>Improve our services and customer experience</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">3. Information Sharing</h2>
              <p>
                Detailing Disciples does not sell, rent, or share your personal information with third parties for marketing purposes. Information may be shared with:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Payment processors (if using online booking)</li>
                <li>Scheduling platforms (Square Appointments, if applicable)</li>
                <li>Service providers necessary to operate our business</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">4. Photo Uploads</h2>
              <p>
                Photos you upload for quote assessment are used solely to evaluate your vehicle's condition and recommend appropriate services. Photos will not be published or used for marketing without your explicit consent.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">5. Data Retention</h2>
              <p>
                We retain your contact and service information for business records and to facilitate future appointments. You may request deletion of your information at any time by contacting us.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">6. Cookies and Analytics</h2>
              <p>
                Our website uses Google Analytics to understand how visitors use our site. This helps us improve the user experience. You can opt out of Google Analytics tracking using browser extensions or settings.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">7. Your Rights</h2>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Request access to your personal information</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt out of marketing communications</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">8. Contact Us</h2>
              <p>
                For questions about this privacy policy or to exercise your rights, contact:
              </p>
              <p className="mt-4">
                <strong className="text-white">Detailing Disciples</strong><br />
                Email: <a href="mailto:info@detailingdisciples.com" className="text-[#B8860B] hover:underline">info@detailingdisciples.com</a><br />
                Phone: <a href="tel:+17473207174" className="text-[#B8860B] hover:underline">(747) 320-7174</a>
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-4">9. Changes to This Policy</h2>
              <p>
                We may update this privacy policy from time to time. Changes will be posted on this page with an updated "Last Updated" date.
              </p>
            </div>

          </div>
        </div>
      </section>

      <section className="py-8 bg-[#1A1A1F]">
        <div className="container mx-auto px-4 text-center">
          <Link to="/" className="text-[#B8860B] hover:text-[#C8960B] font-semibold">
            ← Back to Home
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Privacy;